import { t } from '@app/i18n';
import { useId } from 'react';
import { Field, IconButton, SearchCombobox, TextInput, type ComboOption } from '@zarya/ui';
import { Icon } from '@zarya/theme';
import type { ApiError } from '@app/api/ApiError';
import type { LabPerson } from '@app/main/Conversar';
import { PROFILES, type ClaimsForm } from './ChatModel';
import s from './Chat.module.css';

export type AskerTab = 'person' | 'claims';

export interface WhoAsksProps {
  tab: AskerTab;
  onTab: (tab: AskerTab) => void;
  people: LabPerson[];
  peopleLoading: boolean;
  peopleError: ApiError | null;
  person: string | null;
  onPerson: (sub: string | null) => void;
  claims: ClaimsForm;
  onClaims: (claims: ClaimsForm) => void;
}

/**
 * «Quem pergunta» (§4): a pessoa do lab (JWT pré-assinado do Secret) ou as
 * claims de um JWT que o middleware cunha na hora. As duas abas alimentam o
 * mesmo pedido — exatamente uma delas vai —, e o cartão «token da pessoa» ao
 * lado da conversa mostra o que a aba escolhida vai mandar.
 */
export function WhoAsks({
  tab,
  onTab,
  people,
  peopleLoading,
  peopleError,
  person,
  onPerson,
  claims,
  onClaims,
}: WhoAsksProps) {
  const base = useId();
  // `id` é o valor do código (`AskerTab`); rótulo e dica saem do catálogo.
  const tabs: { id: AskerTab; label: string; hint: string }[] = [
    { id: 'person', label: t('chat.who.tab.person'), hint: t('chat.who.tab.personHint') },
    { id: 'claims', label: t('chat.who.tab.claims'), hint: t('chat.who.tab.claimsHint') },
  ];

  const personOptions: ComboOption[] = people.map((p) => ({
    value: p.sub,
    label: t('chat.who.personOption', { sub: p.sub, profile: p.profile }),
    hint: t('chat.who.personHint', { aud: p.aud }),
  }));
  const profileOptions: ComboOption[] = PROFILES.map((p) => ({ value: p, label: p, mono: true }));

  return (
    <div className={s.who}>
      <div className={s.tabs} role="tablist" aria-label={t('chat.who.tablistLabel')}>
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`${base}-tab-${t.id}`}
            aria-selected={tab === t.id}
            aria-controls={`${base}-panel-${t.id}`}
            className={`${s.tab} ${tab === t.id ? s.tabOn : ''}`}
            onClick={() => onTab(t.id)}
          >
            <span>{t.label}</span>
            <span className={s.tabHint}>{t.hint}</span>
          </button>
        ))}
      </div>

      {tab === 'person' ? (
        <div role="tabpanel" id={`${base}-panel-person`} aria-labelledby={`${base}-tab-person`}>
          <Field
            label={t('chat.who.personLabel')}
            required
            tip={t('chat.who.personTip')}
          >
            {({ id, className: _class, ...aria }) => (
              <SearchCombobox
                id={id}
                {...aria}
                label={t('chat.who.personLabel')}
                options={personOptions}
                value={person}
                placeholder={
                  peopleLoading
                    ? t('chat.who.personLoading')
                    : peopleError
                      ? t('chat.who.personUnavailable')
                      : t('chat.who.personPlaceholder')
                }
                disabled={Boolean(peopleError)}
                emptyMessage={t('chat.who.personEmpty')}
                onChange={onPerson}
              />
            )}
          </Field>
        </div>
      ) : (
        <div
          role="tabpanel"
          id={`${base}-panel-claims`}
          aria-labelledby={`${base}-tab-claims`}
          className={s.claimsForm}
        >
          <Field label="sub" required tip={t('chat.who.subTip')}>
            {({ id, className, ...aria }) => (
              <TextInput
                id={id}
                className={className}
                {...aria}
                mono
                value={claims.sub}
                placeholder={t('chat.who.subPlaceholder')}
                autoComplete="off"
                onChange={(e) => onClaims({ ...claims, sub: e.target.value })}
              />
            )}
          </Field>

          <Field
            label="perfil"
            tip={t('chat.who.profileTip')}
          >
            {({ id, className: _class, ...aria }) => (
              <SearchCombobox
                id={id}
                {...aria}
                label="perfil"
                options={profileOptions}
                value={claims.profile}
                clearable
                placeholder={t('chat.who.profilePlaceholder')}
                emptyMessage={t('chat.who.profileEmpty')}
                onChange={(profile) => onClaims({ ...claims, profile })}
              />
            )}
          </Field>

          <div className={s.extras}>
            <div className={s.extrasHead}>
              <span className={s.extrasLabel}>{t('chat.who.otherClaims')}</span>
              <button
                type="button"
                className={s.addPair}
                onClick={() => onClaims({ ...claims, extras: [...claims.extras, { key: '', value: '' }] })}
              >
                <Icon name="plus" size="sm" aria-hidden="true" />
                {t('chat.who.addClaim')}
              </button>
            </div>
            {claims.extras.length === 0 ? (
              <p className={s.extrasEmpty}>{t('chat.who.noOtherClaims')}</p>
            ) : null}
            {claims.extras.map((pair, i) => (
              <div key={i} className={s.pair}>
                <TextInput
                  mono
                  aria-label={t('chat.who.claimKey', { n: i + 1 })}
                  placeholder={t('chat.who.claimKeyPlaceholder')}
                  value={pair.key}
                  autoComplete="off"
                  onChange={(e) =>
                    onClaims({
                      ...claims,
                      extras: claims.extras.map((p, j) => (j === i ? { ...p, key: e.target.value } : p)),
                    })
                  }
                />
                <TextInput
                  mono
                  aria-label={t('chat.who.claimValue', { n: i + 1 })}
                  placeholder={t('chat.who.claimValuePlaceholder')}
                  value={pair.value}
                  autoComplete="off"
                  onChange={(e) =>
                    onClaims({
                      ...claims,
                      extras: claims.extras.map((p, j) => (j === i ? { ...p, value: e.target.value } : p)),
                    })
                  }
                />
                <IconButton
                  icon="trash"
                  label={t('chat.who.removeClaim', { n: i + 1 })}
                  size="sm"
                  onClick={() => onClaims({ ...claims, extras: claims.extras.filter((_, j) => j !== i) })}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
